import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import {
  BeneficiaryFilterFullTextType,
  BeneficiaryFilterItem,
  FilterTemplate,
  SortOption,
} from '@repo/shared/dtos/beneficiary'
import { sortByKey } from '@repo/ui/utils'

import { adminStoreActions, storeActions } from '@/store/actions'
import { CurrentFilterTemplate } from '@/store/beneficiary-list-filters/types'
import { defaultWidget } from '@/store/dashboard-widgets/slice'

import { Beneficiary } from '../beneficiary'
import {
  createFilterTemplate,
  deleteFilterTemplate,
  getBeneficiaries,
  getFilterTemplates,
  updateFilterTemplate,
} from './thunks'

export const defaultFilterTemplate: CurrentFilterTemplate = {
  name: '',
  filters: [
    {
      key: 'content',
      type: 'fullText',
      value: '',
    } satisfies BeneficiaryFilterFullTextType,
  ],
}

interface State {
  beneficiaries: Beneficiary[]
  currentFilterTemplate: CurrentFilterTemplate
  loading: boolean
  pagination: {
    page: number
    perPage: number
    maxPage: number
    totalItems: number
  }
  savedFilterTemplates: FilterTemplate[]
  scope: 'admin' | 'caseworker'
  sortOption: SortOption
}

const initialPage = 1
const initialPerPage = 10
const initialState: State = {
  beneficiaries: [],
  currentFilterTemplate: defaultFilterTemplate,
  loading: true,
  pagination: {
    page: initialPage,
    perPage: initialPerPage,
    maxPage: 0,
    totalItems: 0,
  },
  savedFilterTemplates: [],
  scope: 'caseworker',
  sortOption: {
    field: 'fullName',
    direction: 'ASC',
  },
}

function safeResetState(state: State) {
  const savedFilterTemplates = state.savedFilterTemplates
  const scope = state.scope
  Object.assign(state, {
    ...initialState,
    sortOption: state.sortOption,
    savedFilterTemplates,
    scope,
  })
}

const slice = createSlice({
  name: 'beneficiaryList',
  initialState,
  reducers: {
    addFilter(state, action: PayloadAction<BeneficiaryFilterItem>) {
      state.currentFilterTemplate.filters.push(action.payload)
    },
    removeFilter(state, action: PayloadAction<BeneficiaryFilterItem>) {
      state.currentFilterTemplate.filters = state.currentFilterTemplate.filters.filter(
        (filter) => filter.key !== action.payload.key,
      )
    },
    updateFilter(state, action: PayloadAction<BeneficiaryFilterItem>) {
      const index = state.currentFilterTemplate.filters.findIndex(
        (filter) => filter.key === action.payload.key,
      )
      if (index !== -1) {
        state.currentFilterTemplate.filters[index] = action.payload
      }
    },
    updateSearchTextFilter(state, action: PayloadAction<string>) {
      const existingIndex = state.currentFilterTemplate.filters.findIndex(
        (filter) => filter.key === 'content' && filter.type === 'fullText',
      )
      if (existingIndex >= 0) {
        // Update
        // @ts-expect-error type fullText has value string
        state.currentFilterTemplate.filters[existingIndex]!.value = action.payload
      } else {
        // Create
        state.currentFilterTemplate.filters.push({
          key: 'content',
          type: 'fullText',
          value: action.payload,
        } satisfies BeneficiaryFilterFullTextType)
      }
    },
    setCurrentFilterTemplate(state, action: PayloadAction<State['currentFilterTemplate']>) {
      state.loading = true
      state.currentFilterTemplate = action.payload
      state.pagination.page = initialPage
    },
    setSortOption(state, action: PayloadAction<State['sortOption']>) {
      state.loading = true
      state.sortOption = action.payload
    },
    setPage(state, action: PayloadAction<State['pagination']['page']>) {
      state.loading = true
      state.pagination.page = action.payload
    },
    reset(state) {
      safeResetState(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      storeActions.initOrganization.pending,
      (
        _,
        {
          meta: {
            arg: { isSameOrganization },
          },
        },
      ) => {
        if (isSameOrganization) return
        return initialState
      },
    )

    builder
      .addCase(getBeneficiaries.pending, (state, action) => {
        state.currentFilterTemplate = action.meta.arg
      })
      .addCase(getBeneficiaries.fulfilled, (state, action) => {
        state.loading = false
        state.beneficiaries = action.payload.beneficiaries
        state.pagination.maxPage = action.payload.meta.maxPage
        state.pagination.totalItems = action.payload.meta.totalItems
      })
      .addCase(getBeneficiaries.rejected, (state) => {
        state.loading = false
      })

    builder
      .addCase(deleteFilterTemplate.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteFilterTemplate.fulfilled, (state, action) => {
        state.savedFilterTemplates = state.savedFilterTemplates.filter(
          (template) => template.id !== action.meta.arg,
        )
      })

    builder.addCase(getFilterTemplates.fulfilled, (state, action) => {
      state.savedFilterTemplates = structuredClone([
        defaultWidget.filterTemplate,
        ...action.payload,
      ]).sort(sortByKey('name'))
    })

    builder.addCase(
      createFilterTemplate.fulfilled,
      (state, action: PayloadAction<FilterTemplate>) => {
        const filterTemplate = action.payload
        state.currentFilterTemplate = filterTemplate
        state.savedFilterTemplates.push(filterTemplate)
        state.savedFilterTemplates.sort(sortByKey('name'))
      },
    )

    builder.addCase(updateFilterTemplate.fulfilled, (state, action) => {
      state.currentFilterTemplate = action.payload
    })

    builder.addCase(adminStoreActions.initAdmin.pending, (state) => {
      safeResetState(state)
      state.scope = 'admin'
    })

    builder.addMatcher(
      isAnyOf(storeActions.initDashboard, storeActions.beneficiary.initBeneficiary.pending),
      (state) => {
        safeResetState(state)
        state.scope = 'caseworker'
      },
    )
  },
})

export const {
  addFilter,
  removeFilter,
  reset,
  setCurrentFilterTemplate,
  setPage,
  setSortOption,
  updateFilter,
  updateSearchTextFilter,
} = slice.actions
export type { State as BeneficiaryListState }
export const beneficiaryListInitialState = initialState
export default slice.reducer
